import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLeaveRequests } from '../hooks/useLeaveRequests'
import { LeaveRequestDrawer } from './LeaveRequestDrawer'
import { LeaveRequestsCard } from './LeaveRequestsCard'

interface LeaveTabProps {
  onReviewLeave?: (id: string) => void
  onViewLeave?: (id: string) => void
  onViewBalanceHistory?: () => void
}

export function LeaveTab({
  onReviewLeave,
  onViewLeave,
}: LeaveTabProps) {
  const navigate = useNavigate()
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null)
  const [drawerMode, setDrawerMode] = useState<'review' | 'view'>('review')

  const {
    loading,
    page,
    setPage,
    limit,
    total,
    typeFilter,
    setTypeFilter,
    statusFilter,
    setStatusFilter,
    fromDate,
    handleFromDateChange,
    toDate,
    handleToDateChange,
    handleClearFilters,
    filteredRequests,
    policyOptions,
    handleApproveRequest,
    handleRejectRequest,
    isReviewing,
  } = useLeaveRequests()

  const selectedRequest =
    filteredRequests.find((r) => r.id === selectedRequestId) || null

  const handleReview = (id: string) => {
    setSelectedRequestId(id)
    setDrawerMode('review')
    onReviewLeave?.(id)
  }

  const handleView = (id: string) => {
    setSelectedRequestId(id)
    setDrawerMode('view')
    onViewLeave?.(id)
  }

  const handleApprove = async (id: string, remarks?: string) => {
    try {
      await handleApproveRequest(id, remarks)
      setSelectedRequestId(null)
    } catch {
      // Mutation handles error toasts and invalidations
    }
  }

  const handleReject = async (id: string, reason: string, remarks?: string) => {
    try {
      await handleRejectRequest(id, reason, remarks)
      setSelectedRequestId(null)
    } catch {
      // Mutation handles error toasts and invalidations
    }
  }

  if (loading && filteredRequests.length === 0) {
    return (
      <div className="bg-white rounded-2xl p-12 border border-slate-200 shadow-xs text-center text-slate-500 min-w-0 font-sans">
        Loading leave requests...
      </div>
    )
  }

  return (
    <div className="space-y-6 min-w-0 font-sans">
      <LeaveRequestsCard
        requests={filteredRequests}
        typeFilter={typeFilter}
        onTypeFilterChange={setTypeFilter}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        fromDate={fromDate}
        onFromDateChange={handleFromDateChange}
        toDate={toDate}
        onToDateChange={handleToDateChange}
        onClearFilters={handleClearFilters}
        onViewBalanceHistory={() => navigate('/admin/hrms/attendance-leave/leave/leave-balance')}
        onReviewLeave={handleReview}
        onViewLeave={handleView}
        page={page}
        limit={limit}
        total={total}
        onPageChange={setPage}
        typeOptions={policyOptions}
      />

      <LeaveRequestDrawer
        requestId={selectedRequestId}
        request={selectedRequest}
        mode={drawerMode}
        isSubmitting={isReviewing}
        onClose={() => setSelectedRequestId(null)}
        onApprove={handleApprove}
        onReject={handleReject}
      />
    </div>
  )
}
